// Approx 30 frames per second
const SPEED = 30 / 60;

class MarqueeBanner extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement} */
    // @ts-expect-error
    this.track = this.querySelector(".banner__slide-track");

    /** @type {HTMLElement} */
    // @ts-expect-error
    this.plainView = this.querySelector(".banner__slide-plain");

    this.snapshot = this.createSnapshot();

    /** @type {IntersectionObserver|null} */
    this.observer = null;
    this.offset = 0;
    this.isAnimating = true;

    /** @type {HTMLElement|null} */
    this.activeElement = null;

    this.animationFunc = this.animationFunc.bind(this);

    this.addEventListener("pointerover", (e) => {
      if (e.pointerType !== "touch") {
        this.stopAnimation();
      }
    });

    this.addEventListener("pointerout", (e) => {
      if (e.pointerType !== "touch") {
        this.startAnimation();
      }
    });

    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.addEventListener("focus", this.handleFocus);
    this.addEventListener("blur", this.handleBlur);
    this.addEventListener("keydown", this.handleKeyDown);
  }

  connectedCallback() {
    this.fillSlides();
    requestAnimationFrame(this.animationFunc);
  }

  startAnimation() {
    if (this.isAnimating === false) {
      requestAnimationFrame(this.animationFunc);
      this.isAnimating = true;
    }
  }

  stopAnimation() {
    if (this.isAnimating === true) {
      this.isAnimating = false;
    }
  }

  /**
   * @param {KeyboardEvent} e
   * */
  handleKeyDown(e) {
    const key = e.key;
    /** @type {HTMLElement} */
    // @ts-expect-error
    const target = e.target;

    /** @type {NodeListOf<HTMLElement>} */
    const slideLinks = this.plainView.querySelectorAll(".banner__slide a ");

    if (key === "Tab" && slideLinks[slideLinks.length - 1] === target) {
      this.hideAccessibleView();
      this.startAnimation();
      return;
    }
  }

  selectNextSlide() {
    /** @type {NodeListOf<HTMLElement>} */
    const links = this.plainView.querySelectorAll(".banner__slide a");

    if (links.length === 0) {
      return false;
    }

    if (this.activeElement === null) {
      this.activeElement = links[0];
      this.activeElement.focus();
      return true;
    }

    let nextIndex = -1;

    for (let i = 0; i < links.length; i++) {
      if (links[i] === this.activeElement) {
        nextIndex = i + 1;
      }
    }

    if (nextIndex === -1 && links[nextIndex]) {
      links[nextIndex].focus();
      this.activeElement = links[nextIndex];
      return true;
    }
    return false;
  }

  /**
   * @param {FocusEvent} e
   */
  handleBlur(e) {
    const links = [...this.plainView.querySelectorAll(".banner__slide a")];

    /** @type {HTMLElement|null} */
    // @ts-expect-error
    const relatedTarget = e.relatedTarget;

    if (relatedTarget && links.includes(relatedTarget)) {
      return;
    }
    this.startAnimation();
    this.hideAccessibleView();
  }

  /**
   * @param {FocusEvent} e
   */
  handleFocus(e) {
    /** @type {HTMLElement} */
    // @ts-expect-error
    const target = e.target;
    if (target.matches(":focus-visible")) {
      this.stopAnimation();
      this.showAccessibleView();
    }
  }

  showAccessibleView() {
    this.setAttribute("data-mode", "plain");
  }

  hideAccessibleView() {
    this.removeAttribute("data-mode");
  }

  createSnapshot() {
    const div = document.createElement("div");

    this.track.querySelectorAll(".banner__slide").forEach((el) => {
      const clone = el.cloneNode(true);
      div.appendChild(clone);
    });
    return div;
  }

  fillSlides() {
    const width = window.innerWidth;

    /** @type {HTMLElement[]} */
    // @ts-expect-error
    const slides = [...this.track.children];

    const slidesWidth = slides.reduce((acc, x) => acc + x.clientWidth, 0);
    const availableSpace = Math.max(width - slidesWidth, 0);
    const groupsNeeded = Math.ceil(availableSpace / slidesWidth);

    if (isFinite(groupsNeeded)) {
      this.startObserving(groupsNeeded + 2);
      this.insertClones(groupsNeeded + 2);
    } else {
      console.error(`Groups needed is not finite: ${groupsNeeded}`);
    }

    this.classList.add("is-ready");
  }

  animationFunc() {
    if (!this.isAnimating) {
      return;
    }

    this.offset = this.offset - SPEED;
    this.track.style.transform = `translateX(${this.offset}px)`;
    requestAnimationFrame(this.animationFunc);
  }

  /**
   * @param {number} count
   */
  startObserving(count) {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.observer?.unobserve(entry.target);
            this.insertClones(count);
            this.removeOldSlides();
          }
        });
      },
      { root: this, threshold: 0.01 }
    );
  }

  /**
   * @param {number} count
   */
  insertClones(count) {
    for (let i = 0; i < count; i++) {
      for (let k = 0; k < this.snapshot.children.length; k++) {
        const el = this.snapshot.children[k];

        /** @type {HTMLElement} */
        // @ts-expect-error
        const clone = el.cloneNode(true);

        // When last, track slide
        if (i + 1 === count && k + 1 === this.snapshot.children.length) {
          clone.classList.add("is-last-slide");
          /** @type {IntersectionObserver} */
          // @ts-expect-error
          this.observer.observe(clone);
        }

        this.track.insertAdjacentElement("beforeend", clone);
      }
    }
  }

  removeOldSlides() {
    /** @type {HTMLElement[]} */
    // @ts-expect-error
    const slides = [...this.track.children];

    let totalWidth = 0;

    /** @type {HTMLElement[]} */
    let itemsToRemove = [];

    for (const slide of slides) {
      const rect = slide.getBoundingClientRect();
      const endX = rect.left + rect.width;

      if (endX > 0) {
        break;
      }

      totalWidth += slide.clientWidth;
      itemsToRemove.push(slide);
    }

    if (totalWidth > 0) {
      this.offset = this.offset + totalWidth;

      itemsToRemove.forEach((item) => {
        item.remove();
      });
    }
  }
}

customElements.define("marquee-banner", MarqueeBanner);
